import React from "react";

import ZoneError from "./ZoneError";

//? ============================================================================
//? LA BARRIÈRE D'ERREUR DE CE MODULE — posée le 16/09/2026.
//? ============================================================================
//? POURQUOI ELLE EXISTE. Recette du 16/09 : « j'ai cliqué sur setup depuis
//? dashboard organisateur (events) -> page blanche ». Le passage du lien et du
//? jeton, lui, est vérifié et fonctionne (fragment lu par le bloc `HANDOFF` de
//? `views/EventAuth/index.js`, adresse nettoyée derrière, routage correct) : le
//? blanc est EN AVAL du passage.
//?
//? ⚠️ IL N'EXISTAIT AUCUNE BARRIÈRE D'ERREUR DANS TOUT CE MODULE — vérifié avant
//? d'écrire : zéro `componentDidCatch`, zéro `getDerivedStateFromError`, zéro
//? `ErrorBoundary`, et même zéro composant de classe dans les 381 fichiers de
//? `src/`. N'IMPORTE QUELLE exception de n'importe quel composant démontait donc
//? l'arbre entier, et CRA — contrairement à Next — n'a même pas d'écran de
//? dernier recours : il ne reste RIEN. Un document strictement vide.
//?
//? Le blanc n'est pas le défaut : c'est la SIGNATURE d'une exception dans une
//? application qui ne se protège pas. Un organisateur qui perd son écran au
//? milieu d'une configuration ne sait ni ce qu'il a perdu, ni s'il doit
//? recommencer.
//?
//? CE QU'ELLE CHANGE, ET CE QU'ELLE NE CHANGE PAS. Elle ne répare aucun défaut :
//? elle en CONTIENT les effets, et transforme tout défaut futur de cette classe
//? en retour de recette exploitable. La cause du plantage du 16/09 est mesurée
//? ailleurs, dans le navigateur ; ce fichier ne la cherche pas et ne la devine
//? pas.
//?
//? POURQUOI UNE CLASSE. React est en 17.0.2 ici (`package.json`) et
//? l'application est du CRA, pas du Next : il n'y a ni `error.js` de segment ni
//? équivalent en fonction. `getDerivedStateFromError` / `componentDidCatch`
//? n'existent que sur une classe. C'est l'unique composant de classe du module,
//? et c'est la seule raison valable d'en avoir un.
//?
//? UNE BARRIÈRE PAR ZONE, JAMAIS UNE SEULE GLOBALE. Une globale attrape tout et
//? masque donc des plantages ailleurs sans que personne ne les voie : le module
//? paraîtrait sain alors qu'une zone serait morte. Les emplacements sont listés
//? dans `CLAUDE.md`. Chaque zone passe son propre texte — voir `ZoneError.js`.
//?
//? ⚠️⚠️ CETTE BARRIÈRE NE DOIT JAMAIS RECHARGER LA PAGE. C'est la contrainte
//? structurante de ce module, et elle vient du canal de passage du jeton :
//?
//?   - `HANDOFF` (`views/EventAuth/index.js:50`) lit le jeton dans
//?     `window.location.hash` AU CHARGEMENT DU MODULE, puis efface aussitôt le
//?     fragment avec `history.replaceState`.
//?   - Un `window.location.reload()` réévaluerait donc le module avec un fragment
//?     DÉJÀ VIDE : le jeton ne serait plus récupérable que depuis `localStorage`,
//?     et s'il n'y avait pas encore été écrit, l'organisateur repartirait sur
//?     l'écran de connexion — en perdant sa configuration en cours. Une barrière
//?     qui fait ça aggrave exactement ce qu'elle est censée contenir.
//?
//? UN REMONTAGE, LUI, EST SÛR — et c'est ce que fait la sortie ci-dessous.
//? `HANDOFF` est un `const` de PORTÉE MODULE : sa valeur est capturée une fois à
//? l'évaluation du module et survit à n'importe quel nombre de montages et
//? démontages de composants. Réessayer ne relit donc pas le fragment (il n'y a
//? plus rien à relire) et ne le perd pas non plus. Rien ici ne touche à
//? `window.location`, ni au fragment, ni à l'historique.
//?
//? ⚠️ NE PAS CONFONDRE avec l'autre usage du fragment : `EventSetup` se sert du
//? hash comme sélecteur d'onglet (`#info`, `#branding`, `#registration`,
//? `#review` — `views/EventSetup/config.js`). Raison de plus pour qu'une reprise
//? d'erreur n'écrive jamais dans `window.location.hash` : elle changerait
//? d'étape sous les pieds de l'organisateur.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    //? `remountKey` est la clé du sous-arbre : l'incrémenter force React à
    //? DÉMONTER puis REMONTER les enfants, au lieu de les re-rendre avec l'état
    //? local qui vient justement de les faire tomber. Sans ça, « Try again »
    //? rejouerait le même plantage à l'identique.
    this.state = { error: null, remountKey: 0, armedOn: props.resetOn };
    //? Liaison dans le constructeur et non en propriété de classe : `package.json`
    //? porte un bloc `babel` réduit à `@babel/preset-react`, et on ne fait pas
    //? dépendre l'affichage d'un écran d'erreur d'une syntaxe qui aurait besoin
    //? d'un plugin de plus.
    this.handleRetry = this.handleRetry.bind(this);
  }

  //? DÉSARMEMENT À LA NAVIGATION. Sans ceci, une barrière déclenchée reste
  //? affichée : en `react-router` v5, changer de route à l'INTÉRIEUR d'une même
  //? `<Route>` (par exemple `/event-setup#info` -> `/event-setup#branding`) ne
  //? démonte pas la barrière, qui continuerait donc à montrer son erreur sur un
  //? contenu devenu sain. Les zones concernées passent un `resetOn` ; celles qui
  //? n'en passent pas (`undefined` constant) ne s'auto-désarment jamais, ce qui
  //? est le comportement voulu pour un cadre qui ne bouge pas.
  static getDerivedStateFromProps(props, state) {
    if (props.resetOn === state.armedOn) return null;
    return {
      armedOn: props.resetOn,
      error: null,
      remountKey: state.error ? state.remountKey + 1 : state.remountKey
    };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    //? La pile complète en console, une fois, pour que le défaut reste
    //? diagnosticable — c'est elle qui manquait à la recette. Le préfixe est
    //? volontairement identique à celui d'`evey.next` : un
    //? `grep "exception contenue par la barrière"` en console les retrouve toutes.
    //? Rien de ceci n'est montré : ces messages nomment des chemins internes et
    //? n'aideraient pas l'organisateur.
    console.error(`[${this.props.zone || "unknown"}] exception contenue par la barrière :`, error, info?.componentStack);
    if (this.props.onError) this.props.onError(error);
  }

  handleRetry() {
    //? Uniquement de l'état local. Aucun `reload`, aucune navigation, aucune
    //? écriture dans `window.location` — voir le pavé en tête de fichier.
    this.setState((prev) => ({ error: null, remountKey: prev.remountKey + 1 }));
    if (this.props.onRetry) this.props.onRetry();
  }

  render() {
    const { error } = this.state;
    const { title, message, retryLabel, compact, actions, children } = this.props;

    if (error) {
      return (
        <ZoneError title={title} message={message} retryLabel={retryLabel} compact={compact} onRetry={this.handleRetry}>
          {actions || null}
        </ZoneError>
      );
    }

    return <React.Fragment key={this.state.remountKey}>{children}</React.Fragment>;
  }
}

export default ErrorBoundary;
